import React, { ReactElement, useEffect, useMemo } from "react";
import { TicketMessage, TicketMessageType } from "../../../__generated__/api";
import MessageCard from "./message-card";
import SystemMessageCard from "./system-message-card";
import Styles from "../Chat.module.scss";
import NoteCard from "./note-card";
import useChatState from "../chat-state";
import { EditMessage } from "../card-actions/edit";

export interface CardProps {
    chatId: string;
    messages: TicketMessage[];
    isEditable: boolean;
}
export default function Cards({
    chatId, messages, isEditable,
}: CardProps): ReactElement {
    let last: TicketMessage | undefined;
    const lastRef = useMemo(() => React.createRef<HTMLDivElement>(), [messages.length]);

    const moveBottom = () => {
        if (lastRef?.current) {
            lastRef.current.scrollTo({
                top: lastRef.current.scrollHeight,
            });
        }
    };
    const state = useChatState();

    const dedupedMessages = useMemo(() => {
        const deduped = messages.dedupe(msg => msg.id);
        const agentMessages = deduped
            .filter(msg => msg.type === TicketMessageType.AgentMessage);
        state.setLastMessageId(agentMessages.lastElement?.id);
        return deduped;
    }, [messages]);

    useEffect(() => {
        if (!lastRef.current) {
            return undefined;
        }

        moveBottom();

        const listeners: (() => void)[] = [];
        const images = lastRef.current.querySelectorAll("img");
        images.forEach(item => {
            if (!item.complete) {
                item.addEventListener("load", moveBottom);
                listeners.push(() => item.removeEventListener("load", moveBottom));
            }
        });

        return () => {
            listeners.forEach(handler => handler());
        };
    }, [lastRef?.current]);

    useEffect(moveBottom, [messages, messages.length]);

    return (
        <div className={`flex-grow-1 align-self-stretch p-3 overflow-auto ${Styles["chat-container"]}`} ref={lastRef}>
            {messages.isEmpty && (
                <div className="text-light text-center mt-3">
                    No messages
                </div>
            )}

            <EditMessage
                ticketMessageId={state.editingMessageId}
                text={state.editingMessageId
                    ? dedupedMessages.find(msg => msg.id === state.editingMessageId)?.body.html as string
                    : undefined}
            />
            {dedupedMessages.map(msg => {
                const message = msg as TicketMessage;
                const lastCopy = last;
                last = message;
                if (message.type === TicketMessageType.AgentMessage
                    || message.type === TicketMessageType.UserMessage
                    || message.type === TicketMessageType.Triggered) {
                    return (
                        <MessageCard
                            isEditable={isEditable}
                            chatId={chatId}
                            key={msg.id}
                            message={message}
                            previous={lastCopy}
                        />
                    );
                } if (message.type === TicketMessageType.Comment) {
                    return <NoteCard key={msg.id} message={message} />;
                } if (message.type !== TicketMessageType.Draft) {
                    return <SystemMessageCard key={msg.id} message={message} />;
                }
                return null;
            })}
        </div>
    );
}
